/**
 * @file components/common/SignaturePadModal.tsx
 * @description 通用「手寫簽名」彈窗。
 *
 * 在 Modal 內提供一塊 Canvas，使用者可以鼠標／觸控筆／手指簽名。
 * 點擊「完成簽字」時會把 Canvas 轉成 PNG File 物件，透過 onSubmit 回拋給呼叫方。
 *
 * 設計：
 *   - 簽名區固定 600x200 px（容器自適應寬度），白底黑筆。
 *   - 底部三顆按鈕：清除 / 取消 / 完成簽字。
 *   - 簽名為空時「完成簽字」按鈕禁用。
 *   - 上傳/提交期間「完成簽字」按鈕進入 loading 狀態。
 *
 * 用法範例：
 * ```tsx
 * <SignaturePadModal
 *   open={open}
 *   title="請簽名確認"
 *   onCancel={() => setOpen(false)}
 *   loading={mutation.isPending}
 *   onSubmit={async (pngFile) => {
 *     const uploaded = await uploadFile(pngFile, { scope: 'misc' });
 *     await mutation.mutateAsync({ signatureFileId: uploaded.id });
 *   }}
 * />
 * ```
 */

import { Button, Modal, Space } from 'antd';
import { message } from '@/lib/antd-app';
import { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

/** SignaturePadModal Props。 */
export interface SignaturePadModalProps {
  /** 是否開啟 Modal */
  open: boolean;
  /** Modal 標題，預設「手寫簽名」 */
  title?: string;
  /** 關閉/取消回調 */
  onCancel: () => void;
  /**
   * 簽名完成回調。會收到一個 PNG File 物件，呼叫方負責上傳/提交。
   * 回傳 Promise 時，按鈕會自動進入 loading 狀態；
   * Promise 解析成功時 Modal 自動關閉，失敗時保持開啟讓使用者重試。
   */
  onSubmit: (signaturePngFile: File) => Promise<void> | void;
  /** 外部傳入的 loading 狀態（與內部 submitting 取 OR） */
  loading?: boolean;
  /** 提交按鈕文字，預設「完成簽字」 */
  submitText?: string;
}

/** 簽名區 Canvas 像素尺寸（CSS 寬度 100% 自適應 Modal 內容區）。 */
const CANVAS_HEIGHT = 220;

/**
 * 將 dataURL 轉為 PNG File。
 * 不依賴 fetch，避免某些受限環境下對 data: URI 的 CSP 限制。
 */
function dataUrlToPngFile(dataUrl: string, filename: string): File {
  // SignaturePad.toDataURL() 必定回傳 "data:<mime>;base64,<payload>"；
  // 仍做拆分守衛以滿足 noUncheckedIndexedAccess，並在意外輸入時拋出可讀錯誤。
  const commaIdx = dataUrl.indexOf(',');
  if (commaIdx === -1) {
    throw new Error('Invalid data URL: missing comma separator');
  }
  const meta = dataUrl.slice(0, commaIdx);
  const b64 = dataUrl.slice(commaIdx + 1);
  const mime = /data:(.*?);base64/.exec(meta)?.[1] ?? 'image/png';
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
}

/**
 * 手寫簽名 Modal 組件。
 */
export function SignaturePadModal({
  open,
  title = '手寫簽名',
  onCancel,
  onSubmit,
  loading = false,
  submitText = '完成簽字',
}: SignaturePadModalProps) {
  const sigRef = useRef<SignatureCanvas | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // 用於控制「完成簽字」按鈕是否可用：簽名為空時禁用
  const [isEmpty, setIsEmpty] = useState(true);
  // canvas 寬度跟隨容器寬度，避免被裁切；Modal 打開後再讀真實寬度
  const [canvasWidth, setCanvasWidth] = useState(560);

  // Modal 打開時重置狀態並測量寬度
  useEffect(() => {
    if (!open) return;
    setIsEmpty(true);
    // 讓 React 先渲染出容器，下個微任務再讀寬度
    const id = window.requestAnimationFrame(() => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth;
        if (w > 0) setCanvasWidth(w);
      }
      sigRef.current?.clear();
    });
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  /** 清除目前的簽名軌跡。 */
  const handleClear = () => {
    sigRef.current?.clear();
    setIsEmpty(true);
  };

  /**
   * 完成簽字：
   * 1. 取得 PNG dataURL → File
   * 2. 呼叫 onSubmit；若回傳 Promise 則等待，期間 loading
   * 3. 成功後重置簽名（呼叫方應該負責關閉 Modal）
   */
  const handleSubmit = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      message.warning('請先簽名');
      return;
    }
    // 用 toDataURL 取去除空白邊距後的圖片，避免大片透明
    const dataUrl = sigRef.current.getCanvas().toDataURL('image/png');
    const file = dataUrlToPngFile(dataUrl, `signature-${Date.now()}.png`);
    try {
      setSubmitting(true);
      await onSubmit(file);
    } catch (err) {
      // 由呼叫方決定 message；這裡只記錄
      console.error('signature submit failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || loading;

  return (
    <Modal
      title={title}
      open={open}
      onCancel={() => {
        if (busy) return;
        onCancel();
      }}
      footer={null}
      destroyOnClose
      width={640}
      mask={{ closable: !busy }}
    >
      <div className="text-sm text-gray-500 mb-2">請在下方框內手寫簽名（可使用滑鼠或觸控）</div>
      <div
        ref={containerRef}
        className="w-full rounded border border-gray-300 bg-white"
        style={{ height: CANVAS_HEIGHT, touchAction: 'none' }}
      >
        <SignatureCanvas
          ref={(r) => {
            sigRef.current = r;
          }}
          penColor="#111827"
          backgroundColor="#ffffff"
          canvasProps={{
            width: canvasWidth,
            height: CANVAS_HEIGHT,
            style: { display: 'block', width: '100%', height: '100%' },
          }}
          onEnd={() => setIsEmpty(sigRef.current?.isEmpty() ?? true)}
        />
      </div>

      <div className="mt-4 flex justify-between">
        <Button onClick={handleClear} disabled={busy || isEmpty}>
          清除
        </Button>
        <Space>
          <Button onClick={onCancel} disabled={busy}>
            取消
          </Button>
          <Button type="primary" onClick={handleSubmit} loading={busy} disabled={isEmpty}>
            {submitText}
          </Button>
        </Space>
      </div>
    </Modal>
  );
}

export default SignaturePadModal;
